import React, { useState } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button
} from "@mui/material"
import { supabaseAdminClient } from "../../Supabase/supabaseRealtimeClient"
import { sendInvite } from "../../Supabase/supabaseClient"

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY

async function criarDependente(body: any) {
  const response = await fetch(`${supabaseUrl}/rest/v1/dependentes`, {
    method: "POST",
    headers: {
      apikey: String(serviceKey),
      Authorization: `Bearer ${serviceKey}`,
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.message || `Erro ${response.status}`)
  }
  return response.json()
}

async function atualizarDependente(id: number, body: any) {
  const response = await fetch(`${supabaseUrl}/rest/v1/dependentes?id=eq.${id}`, {
    method: "PATCH",
    headers: {
      apikey: String(serviceKey),
      Authorization: `Bearer ${serviceKey}`,
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.message || `Erro ${response.status}`)
  }
  return response.json()
}

async function verificarEmailExistente(email: string) {
  const response = await fetch(
    `${supabaseUrl}/rest/v1/dependentes?email=eq.${encodeURIComponent(email)}`,
    {
      headers: {
        apikey: String(serviceKey),
        Authorization: `Bearer ${serviceKey}`
      }
    }
  )
  const data = await response.json()
  return data.length > 0
}

export interface Dependente {
  id: number
  user_id: string
  cliente_id: number
  nome: string
  email: string
}

interface DependenteDialogProps {
  open: boolean
  onClose: () => void
  onSave: (dependente: any) => void
  titularId: string | number
  initialData?: any
  isEditing?: boolean
}

const DependenteDialog: React.FC<DependenteDialogProps> = ({
  open,
  onClose,
  onSave,
  titularId,
  initialData,
  isEditing: propIsEditing
}) => {
  const isEditing = propIsEditing ?? !!initialData
  const [nome, setNome] = useState(initialData?.nome || "")
  const [email, setEmail] = useState(initialData?.email || "")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setNome(initialData?.nome || "")
      setEmail(initialData?.email || "")
      setPassword("")
      setEmailError(null)
    }
  }, [initialData, open])

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSave = async () => {
    const trimmedEmail = email.trim()
    const registrationPassword = password;

    if (!nome || !trimmedEmail || (!isEditing && !registrationPassword)) {
      alert("Por favor, preencha todos os campos obrigatórios.")
      return
    }
    if (!validateEmail(trimmedEmail)) {
      setEmailError("Digite um e-mail válido")
      return
    }

    setLoading(true)
    try {
      if (isEditing && initialData) {
        const result = await atualizarDependente(initialData.id, { nome, email: trimmedEmail })
        alert("Dependente atualizado com sucesso!")
        onSave(result[0])
      } else {
        const existente = await verificarEmailExistente(trimmedEmail)
        if (existente) {
          alert("Este e-mail já está em uso na tabela de dependentes.")
          setLoading(false)
          return
        }

        // Criar novo usuário no Auth e no Banco via Edge Function (Atômico)
        const tableData = {
          cliente_id: titularId,
          nome,
          email: trimmedEmail
        };
        
        const { user, record } = await sendInvite(trimmedEmail, registrationPassword, 'dependente', tableData)
        if (user && record) {
          alert("Dependente criado com sucesso!")
          onSave(record)
        }
      }
      onClose()
    } catch (error: any) {
      console.error(error)
      alert(`Erro: ${error.message || "Erro desconhecido"}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEditing ? "Editar Dependente" : "Adicionar Dependente"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nome"
          fullWidth
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          required
        />
        <TextField
          margin="dense"
          label="E-mail"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setEmailError(validateEmail(e.target.value) ? null : "E-mail inválido")
          }}
          required
          error={!!emailError}
          helperText={emailError}
        />
        {!initialData?.id && (
          <TextField
            margin="normal"
            label="Senha"
            type="password"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !!emailError}>
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DependenteDialog
